// Barème officiel du Polyar'Trottons.
// "group: true" signifie que le montant est multiplié par le nombre de personnes
// (ex : une sortie course faite à plusieurs). "group: false" signifie que le montant
// ne dépend que de la quantité déclarée (ex : nombre de dictées réalisées par la
// personne qui soumet, indépendamment de la taille du groupe).
//
// "seuilPlausible" : au-delà de cette valeur, la participation part en revue
// manuelle (statut "a_valider") plutôt que d'être validée automatiquement.
// Ces seuils sont volontairement larges pour ne jamais pénaliser un effort réel
// (ex : 250 km à vélo en une journée reste tout à fait possible), l'objectif
// est seulement d'attraper les valeurs manifestement aberrantes ou les erreurs
// de saisie (ex : un zéro de trop).

export const BAREMES = {
  marche: {
    label: "Marche, course, vélo",
    unit: "km",
    tarif: 1,
    group: true,
    seuilPlausible: 350,
  },
  fitness: {
    label: "Fitness",
    unit: "répétitions",
    tarif: 1 / 20,
    group: true,
    seuilPlausible: 500,
  },
  dictee: {
    label: "Dictée",
    unit: "dictées",
    tarif: 1,
    group: false,
    seuilPlausible: 20,
  },
  qcm: {
    label: "QCM",
    unit: "QCM",
    tarif: 0.5,
    group: false,
    seuilPlausible: 20,
  },
  enigme: {
    label: "Énigme",
    unit: "énigmes",
    tarif: 0.2, // 1 énigme = 0,20 € (visuel officiel de l'édition 2026)
    group: false,
    seuilPlausible: 20,
  },
  dessin: {
    label: "Dessin",
    unit: "dessins",
    tarif: 1,
    group: false,
    seuilPlausible: 15,
  },
  // Cours collectif ou match : 5 € par participant (et par cours).
  // Quantité = nombre de cours ou de matchs (PAS le nombre de participants,
  // qui vient du champ "Nombre de participants"). Ex. : 1 cours de 20 = 100 €.
  autre: {
    label: "Autre (cours collectif, match)",
    unit: "cours ou matchs",
    tarif: 5,
    group: true,
    seuilPlausible: 3,
  },
};

// Au-delà de ce nombre de personnes sur une même soumission, revue manuelle
// également, quel que soit le type d'action (indépendant du seuil par type).
export const SEUIL_NOMBRE_PERSONNES = 50;

// PLAFONDS ABSOLUS : au-delà, la saisie est refusée (erreur de frappe ou
// déclaration fantaisiste). Ils sont très larges (environ 10 fois les seuils
// de contrôle) pour ne jamais bloquer une vraie performance.
export const PLAFONDS = {
  marche: 3000, // km par personne
  fitness: 5000, // répétitions par personne
  dictee: 200,
  qcm: 200,
  enigme: 200,
  dessin: 150,
  autre: 10, // cours ou matchs (au-delà : c'est sans doute le nombre de participants tapé par erreur)
};
export const PLAFOND_NOMBRE_PERSONNES = 1000;

/**
 * Calcule le montant en euros pour une action donnée.
 * @param {string} type - clé du barème (ex: "marche")
 * @param {number} quantite - valeur déclarée par le participant
 * @param {number} nombrePersonnes - nombre de personnes concernées par la soumission
 * @returns {number} montant arrondi à 2 décimales
 */
export function computeMontant(type, quantite, nombrePersonnes) {
  const bareme = BAREMES[type];
  if (!bareme || isNaN(quantite)) return 0;
  const multiplicateur = bareme.group ? Math.max(1, nombrePersonnes) : 1;
  return Math.round(quantite * bareme.tarif * multiplicateur * 100) / 100;
}

/**
 * Détermine si une participation doit être validée automatiquement ou
 * envoyée en revue manuelle, par une règle simple sans IA (seuils de
 * plausibilité par type d'action).
 * @returns {{ statut: "valide_auto"|"a_valider", motif: string|null }}
 */
export function evaluerPlausibilite(type, quantite, nombrePersonnes) {
  const bareme = BAREMES[type];
  if (!bareme) return { statut: "a_valider", motif: "Type d'action inconnu" };

  if (nombrePersonnes > SEUIL_NOMBRE_PERSONNES) {
    return {
      statut: "a_valider",
      motif: `Groupe important (${nombrePersonnes} personnes, seuil ${SEUIL_NOMBRE_PERSONNES})`,
    };
  }

  if (quantite > bareme.seuilPlausible) {
    return {
      statut: "a_valider",
      motif: `Quantité au-delà du seuil habituel (${bareme.seuilPlausible} ${bareme.unit})`,
    };
  }

  return { statut: "valide_auto", motif: null };
}
